import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, IconButton, Tooltip, Button, CircularProgress } from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import EditIcon from "@mui/icons-material/Edit";
import Swal from "sweetalert2";

import { ImageAnnotationModal } from "./ImageAnnotationModal";

interface ScreenshotUploaderProps {
  images: File[];
  onChangeImages: (files: File[]) => void;
  maxImages?: number;
}

interface EditingImageState {
  file: File;
  index?: number;
  isNew?: boolean;
}

// Sub-component hiển thị thumbnail an toàn bằng FileReader (không bị lỗi ERR_FILE_NOT_FOUND)
const ImageThumbnailItem: React.FC<{
  file: File;
  index: number;
  onEdit: (file: File, index: number) => void;
  onRemove: (index: number) => void;
}> = ({ file, index, onEdit, onRemove }) => {
  const [previewUrl, setPreviewUrl] = useState<string>("");

  useEffect(() => {
    let isMounted = true;
    const reader = new FileReader();
    reader.onload = (e) => {
      if (isMounted && typeof e.target?.result === "string") {
        setPreviewUrl(e.target.result);
      }
    };
    reader.readAsDataURL(file);

    return () => {
      isMounted = false;
    };
  }, [file]);

  return (
    <Box
      sx={{
        position: "relative",
        width: 82,
        height: 82,
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "action.hover",
      }}
    >
      {previewUrl ? (
        <img
          src={previewUrl}
          alt={`upload-${index}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
          <CircularProgress size={16} />
        </Box>
      )}

      {/* Nút Chỉnh sửa / Khoanh vùng vẽ */}
      <Tooltip title="Vẽ / Khoanh vùng lỗi trên ảnh này">
        <IconButton
          size="small"
          onClick={() => onEdit(file, index)}
          sx={{
            position: "absolute",
            bottom: 3,
            left: 3,
            padding: "3px",
            backgroundColor: "rgba(13, 148, 136, 0.85)",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "#0D9488" },
          }}
        >
          <EditIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>

      {/* Nút Xóa ảnh */}
      <Tooltip title="Xóa ảnh này">
        <IconButton
          size="small"
          onClick={() => onRemove(index)}
          sx={{
            position: "absolute",
            top: 3,
            right: 3,
            padding: "3px",
            backgroundColor: "rgba(0, 0, 0, 0.65)",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.95)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export const ScreenshotUploader: React.FC<ScreenshotUploaderProps> = ({ images, onChangeImages, maxImages = 4 }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const [editingImage, setEditingImage] = useState<EditingImageState | null>(null);

  // Lắng nghe sự kiện paste ảnh từ Clipboard (Ctrl + V hoặc Command + V)
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (editingImage) return;

      const clipboardItems = e.clipboardData?.items;
      if (!clipboardItems) return;

      const newFiles: File[] = [];
      for (let i = 0; i < clipboardItems.length; i++) {
        const item = clipboardItems[i];
        if (item.type.indexOf("image") !== -1) {
          const blob = item.getAsFile();
          if (blob) {
            const pastedFile = new File([blob], `screenshot-${Date.now()}.${blob.type.split("/")[1] || "png"}`, {
              type: blob.type,
            });
            newFiles.push(pastedFile);
          }
        }
      }

      if (newFiles.length > 0) {
        if (images.length + newFiles.length > maxImages) {
          Swal.fire({
            icon: "warning",
            title: "Giới hạn số lượng ảnh",
            text: `Bạn chỉ có thể đính kèm tối đa ${maxImages} ảnh!`,
          });
        }
        const updated = [...images, ...newFiles].slice(0, maxImages);
        onChangeImages(updated);
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [images, onChangeImages, maxImages, editingImage]);

  // Chụp ảnh màn hình web với cơ chế tự sửa text baseline clipping
  const handleCaptureScreen = async () => {
    if (images.length >= maxImages) {
      Swal.fire({
        icon: "warning",
        title: "Đã đạt giới hạn ảnh",
        text: `Bạn chỉ có thể đính kèm tối đa ${maxImages} ảnh!`,
      });
      return;
    }

    setIsCapturing(true);

    // Tạm ẩn các dialog / backdrop / widget
    const dialogs = document.querySelectorAll<HTMLElement>(
      ".MuiDialog-root, .MuiBackdrop-root, #user-report-widget-container"
    );
    dialogs.forEach((el) => {
      el.style.opacity = "0";
      el.style.pointerEvents = "none";
    });

    // Chờ 150ms để DOM cập nhật trạng thái ẩn
    await new Promise((resolve) => setTimeout(resolve, 150));

    try {
      // Sử dụng html2canvas với bộ lọc onclone để sửa triệt để lỗi cắt chữ
      const html2canvasModule = await import("html2canvas");
      const html2canvas = html2canvasModule.default;

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        logging: false,
        scale: 2, // Tăng gấp đôi độ sắc nét
        backgroundColor: "#0B0F19",
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        ignoreElements: (element) => {
          return (
            element.classList.contains("MuiDialog-root") ||
            element.classList.contains("MuiBackdrop-root") ||
            element.id === "user-report-widget-container"
          );
        },
        onclone: (clonedDoc) => {
          // Khắc phục triệt để lỗi cắt chữ (text baseline clipping) của html2canvas
          const allTextElements = clonedDoc.querySelectorAll<HTMLElement>(
            "span, p, a, button, div, h1, h2, h3, h4, h5, h6, .MuiTypography-root, .MuiListItemText-primary"
          );
          allTextElements.forEach((el) => {
            if (el.children.length === 0 && el.textContent?.trim()) {
              el.style.overflow = "visible";
              el.style.paddingTop = "2px";
              el.style.paddingBottom = "2px";
            }
          });

          // Bỏ overflow hidden ở các container sidebar item
          const sidebarItems = clonedDoc.querySelectorAll<HTMLElement>(
            ".MuiListItem-root, .MuiButtonBase-root, [class*='sidebar'], [class*='Sidebar']"
          );
          sidebarItems.forEach((el) => {
            el.style.overflow = "visible";
          });
        },
      });

      canvas.toBlob((blob) => {
        if (blob) {
          const capturedFile = new File([blob], `screen-capture-${Date.now()}.png`, { type: "image/png" });
          // Mở ngay modal chỉnh sửa để user khoanh vùng lỗi
          setEditingImage({ file: capturedFile, isNew: true });
        }
      }, "image/png");
    } catch (error) {
      console.error("Screenshot capture failed:", error);
      Swal.fire({
        icon: "error",
        title: "Lỗi chụp màn hình",
        text: "Không thể chụp màn hình tự động. Bạn có thể sử dụng phím tắt chụp màn hình trên máy rồi dán (Ctrl + V) hoặc tải ảnh lên!",
      });
    } finally {
      // Khôi phục hiển thị dialogs
      dialogs.forEach((el) => {
        el.style.opacity = "";
        el.style.pointerEvents = "";
      });
      setIsCapturing(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const selectedFiles = Array.from(e.target.files);
    const updated = [...images, ...selectedFiles].slice(0, maxImages);
    onChangeImages(updated);
    e.target.value = "";
  };

  const handleRemoveImage = (indexToRemove: number) => {
    const updated = images.filter((_, idx) => idx !== indexToRemove);
    onChangeImages(updated);
  };

  const handleOpenAnnotate = (file: File, index: number) => {
    setEditingImage({ file, index, isNew: false });
  };

  const handleSaveAnnotation = (annotatedFile: File) => {
    if (!editingImage) return;

    if (editingImage.isNew) {
      const updated = [...images, annotatedFile].slice(0, maxImages);
      onChangeImages(updated);
    } else if (editingImage.index !== undefined) {
      const updated = [...images];
      updated[editingImage.index] = annotatedFile;
      onChangeImages(updated);
    }

    setEditingImage(null);
  };

  return (
    <Box>
      {/* Header & Controls */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <AddPhotoAlternateIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={700}>
            Ảnh minh chứng ({images.length}/{maxImages})
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {/* Nút chụp màn hình web hiện tại */}
          <Button
            size="small"
            variant="outlined"
            color="primary"
            disabled={isCapturing || images.length >= maxImages}
            onClick={handleCaptureScreen}
            startIcon={
              isCapturing ? <CircularProgress size={14} color="inherit" /> : <CameraAltIcon fontSize="small" />
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12,
              borderRadius: 2,
              px: 1.5,
              py: 0.4,
            }}
          >
            {isCapturing ? "Đang chụp..." : "Chụp màn hình trang"}
          </Button>

          {/* Dán ảnh Ctrl + V */}
          <Box display="flex" alignItems="center" gap={0.6}>
            <ContentPasteIcon sx={{ fontSize: 13, color: "text.secondary" }} />
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: 11 }}>
              Dán:
            </Typography>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                px: 0.8,
                py: 0.2,
                borderRadius: 1,
                fontSize: 11,
                fontWeight: 800,
                fontFamily: "monospace",
                color: "primary.contrastText",
                backgroundColor: "primary.main",
                boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                lineHeight: 1.2,
              }}
            >
              Ctrl + V
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Grid danh sách ảnh preview + nút thêm từ máy */}
      <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
        {images.map((file, idx) => (
          <ImageThumbnailItem
            key={`${file.name}-${idx}`}
            file={file}
            index={idx}
            onEdit={handleOpenAnnotate}
            onRemove={handleRemoveImage}
          />
        ))}

        {/* Nút Tải ảnh từ thiết bị */}
        {images.length < maxImages && (
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              width: 82,
              height: 82,
              borderRadius: 2,
              border: "2px dashed",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backgroundColor: "background.paper",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "action.hover",
              },
            }}
          >
            <AddPhotoAlternateIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary" fontSize={10} mt={0.5} fontWeight={600}>
              Tải từ máy
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
          </Box>
        )}
      </Box>

      {/* Modal Chỉnh Sửa / Khoanh Vùng Minh Chứng */}
      {editingImage && (
        <ImageAnnotationModal
          open={!!editingImage}
          imageSource={editingImage.file}
          onClose={() => setEditingImage(null)}
          onSave={handleSaveAnnotation}
        />
      )}
    </Box>
  );
};
